import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Gavel, ShieldCheck } from "lucide-react";
import Guidebook from "./Guidebook";
import PouchHUD from "./PouchHUD";
import WorldMap from "./WorldMap";
import { useWorld } from "../state/World";

/**
 * The dock. All standing navigation lives here, bottom-right, in the founder's
 * order: World Map, User's Guide, User's Pouch — then the temporary Judges pill,
 * which is the same size as the others rather than a shouting panel, and simply
 * takes you to the brief below the World.
 */
export const HudDock = ({ inline = false }) => {
  const { me } = useWorld();
  const location = useLocation();
  const nav = useNavigate();

  // In the Cove the wall display owns the frame; the Pouch renders inside the
  // screen chrome instead of floating over the room.
  if (!me || location.pathname === "/playforward/cove") return null;

  const toJudges = () => {
    const go = () =>
      document.getElementById("judges")?.scrollIntoView({ behavior: "smooth" });
    if (location.pathname === "/") return go();
    nav("/");
    setTimeout(go, 500);
  };

  return (
    <div
      className={
        inline
          ? "z-40 mx-auto flex max-w-[1500px] flex-wrap items-center justify-between gap-3 px-6 pb-6 pt-2 md:px-10"
          : "contents"
      }
    >
    <div
      className={
        inline
          ? "flex items-center gap-2"
          : "contents"
      }
    >
    {/* the Judges pill is temporary, so it stands apart from the standing
        navigation — far left, same size, out of the Hall door's way */}
    <motion.button
      type="button"
      data-testid="judges-pill"
      onClick={toJudges}
      title="The Builderfest brief"
      className={
        inline
          ? "glass flex h-10 items-center gap-1.5 rounded-full px-3.5 transition-transform duration-200 hover:scale-105"
          : "glass fixed bottom-4 left-4 z-40 flex h-10 items-center gap-1.5 rounded-full px-3.5 transition-transform duration-200 hover:scale-105 md:bottom-6 md:left-6"
      }
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut", delay: 0.45 }}
    >
      <Gavel size={13} className="text-[var(--stage-gold)]" />
      <span className="mono text-[9px] uppercase tracking-[0.18em] text-white/75">
        Judges
      </span>
    </motion.button>

    {/* Next to the brief, the thing that checks the brief. A judge should never
        have to hunt for the evidence. */}
    <motion.button
      type="button"
      data-testid="proof-pill"
      onClick={() => nav("/proof")}
      title="Check the claims live"
      className={
        inline
          ? "glass flex h-10 items-center gap-1.5 rounded-full px-3.5 transition-transform duration-200 hover:scale-105"
          : "glass fixed bottom-4 left-[7.5rem] z-40 flex h-10 items-center gap-1.5 rounded-full px-3.5 transition-transform duration-200 hover:scale-105 md:bottom-6 md:left-[9.5rem]"
      }
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut", delay: 0.55 }}
    >
      <ShieldCheck size={13} className="text-[var(--stage-gold)]" />
      <span className="mono text-[9px] uppercase tracking-[0.18em] text-white/75">
        Proof
      </span>
    </motion.button>
    </div>

    <motion.div
      data-testid="hud-dock"
      className={
        inline
          ? "flex flex-wrap items-end justify-end gap-2"
          : "fixed bottom-4 right-4 z-40 flex max-w-[calc(100vw-2rem)] flex-wrap items-end justify-end gap-2 md:bottom-6 md:right-6"
      }
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut", delay: 0.3 }}
    >
      {me.toured && <WorldMap />}
      <Guidebook />
      <PouchHUD />
    </motion.div>
    </div>
  );
};

export default HudDock;
